// frontend/src/utils/axiosConfig.ts
import axios, { AxiosError } from 'axios';
import { toast } from './toast'; // Import des react-toastify Utilities

import logger from './logger'; // Import des Logger Utilities

const TOKEN_KEY = 'accessToken';

// Typen für Backend-Fehlerobjekte
interface ErrorResponse {
  error?: {
    message?: string;
    code?: string;
    status?: number;
  };
  message?: string; // Fallback für alte API-Endpunkte
}

// Basis-URL aus Umgebungsvariablen lesen
const baseURL = process.env.REACT_APP_API_BASE_URL || "http://localhost:5000";

// Axios-Instanz erstellen
const token = localStorage.getItem(TOKEN_KEY);
const axiosInstance = axios.create({
  baseURL, // Dynamische Basis-URL
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
    ...(token ? { 'Authorization': `Bearer ${token}` } : {})
  }
});

// Response Interceptor
axiosInstance.interceptors.response.use(
  (response) => {
    return response;
  },
  (error: AxiosError) => {
    // Standardisierte Fehlerbehandlung
    let errorMessage = 'Es ist ein Fehler aufgetreten';
    let errorCode = 'UNKNOWN_ERROR';
    
    // Standardfehler verwenden, wenn keine Antwort verfügbar
    if (!error.response) {
      toast.error('Netzwerkfehler. Bitte überprüfen Deine Verbindung.');
      return Promise.reject(error);
    }
    
    // Backend-Fehlerstruktur extrahieren
    const status = error.response.status;
    const responseData = error.response.data as ErrorResponse;
    const errorData = responseData?.error || { message: responseData?.message };
    
    // Fehlerbehandlung nach HTTP-Status
    switch (status) {
      case 400:
        errorMessage = errorData.message || 'Ungültige Anfrage';
        errorCode = errorData.code || 'BAD_REQUEST';
        toast.error(errorMessage);
        break;
        
      case 401:
        errorMessage = errorData.message || 'Nicht autorisiert';
        errorCode = errorData.code || 'UNAUTHORIZED';
        
        // Bei Token-Problemen automatisch abmelden
        if (errorCode === 'TOKEN_EXPIRED' || errorCode === 'INVALID_TOKEN') {
          if (window.location.pathname !== '/') {
            toast.warning('Deine Sitzung ist abgelaufen. Bitte melde Dich erneut an.');
            setTimeout(() => {
              window.location.href = '/';
            }, 1500);
          }
        } else {
          toast.error(errorMessage);
        }
        break;
        
      case 403:
        errorMessage = errorData.message || 'Zugriff verweigert';
        errorCode = errorData.code || 'FORBIDDEN';
        toast.error(errorMessage);
        break;
        
      case 404:
        errorMessage = errorData.message || 'Ressource nicht gefunden';
        errorCode = errorData.code || 'NOT_FOUND';
        toast.error(errorMessage);
        break;
        
      case 500:
      case 502:
      case 503:
        errorMessage = 'Es ist ein Serverfehler aufgetreten';
        errorCode = errorData.code || 'SERVER_ERROR';
        toast.error(errorMessage);
        break;
        
      default:
        toast.error(errorMessage);
    }
    
    // Formatierte Fehlerobjekt für besseres Debugging
    const formattedError = {
      status,
      code: errorCode,
      message: errorMessage,
      originalError: error
    };
    
    // Nur im Entwicklungsmodus in der Konsole anzeigen
    if (process.env.NODE_ENV === 'development') {
      //console.debug('API Error:', formattedError);
      logger.debug('API Error', formattedError  );  
    }
    
    return Promise.reject(formattedError);
  }
);


// Wir definieren eine Event-basierte Lösung für den Logout
// Diese verhindert eine zirkuläre Abhängigkeit zwischen axiosConfig und AuthContext
export const authEvents = {
  onLogout: () => {},
  // Methode zum Registrieren des Logout-Handlers
  registerLogoutHandler: (handler: () => void) => {
    authEvents.onLogout = handler;
  }
};

export default axiosInstance;